'use client'

import { Button } from '@ui/base/button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
} from '@ui/base/combobox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { PlusIcon } from 'lucide-react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { cityOptionLabel, useCitySearch } from '../lib/use-city-search'
import { type FulfillmentFormValues, type LocationForm } from '../model/types'
import { CityOptionText } from './city-option-text'
import { RequiredMark } from './required-mark'

/** Столько складов хватает даже сети: столько же разрешает и сервер. */
const maxLocationCount = 20

type RowProps = {
  index: number
  location: LocationForm
  canRemove: boolean
  onChange: (location: LocationForm) => void
  onRemove: () => void
}

const LocationRow = ({ index, location, canRemove, onChange, onRemove }: RowProps) => {
  const cityFieldId = `fulfillment-location-city-${index}`
  const addressFieldId = `fulfillment-location-address-${index}`
  const requestErrorId = `${cityFieldId}-request-error`
  const search = useCitySearch()
  const selectedLabel = location.city ? `${location.city.name}, ${location.city.regionName}` : ''

  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
      <Field data-invalid={Boolean(search.requestError)}>
        <FieldLabel htmlFor={cityFieldId}>
          Город склада <RequiredMark />
        </FieldLabel>
        <Combobox
          items={search.options}
          filter={null}
          value={null}
          onValueChange={(option) => {
            if (option) {
              onChange({
                ...location,
                city: { id: option.id, name: option.name, regionName: option.region_name },
              })
              search.reset()
            }
          }}
          inputValue={search.query || selectedLabel}
          onInputValueChange={(value, details) => {
            if (details.reason === 'input-change') {
              search.changeQuery(value)
            }
          }}
          itemToStringLabel={cityOptionLabel}
          open={search.isPopupOpen}
          onOpenChange={search.setIsListOpen}
          openOnInputClick={false}
        >
          <ComboboxInput
            id={cityFieldId}
            aria-invalid={Boolean(search.requestError)}
            aria-describedby={search.requestError ? requestErrorId : undefined}
            placeholder="Например, Воронеж"
          />
          <ComboboxPositioner>
            <ComboboxPopup>
              <ComboboxEmpty>Город не найден</ComboboxEmpty>
              <ComboboxList>
                {search.options.map((option) => (
                  <ComboboxItem key={option.id} value={option}>
                    <ComboboxItemIndicator />
                    <CityOptionText option={option} />
                  </ComboboxItem>
                ))}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </Combobox>
        <FieldError id={requestErrorId}>{search.requestError}</FieldError>
      </Field>

      <Field>
        <FieldLabel htmlFor={addressFieldId}>Адрес склада</FieldLabel>
        <Input
          id={addressFieldId}
          value={location.address}
          maxLength={250}
          placeholder="ул. Дорожная, 17"
          onChange={(event) => onChange({ ...location, address: event.target.value })}
        />
      </Field>

      {canRemove && (
        <Button
          type="button"
          variant="outline"
          aria-label={`Убрать склад ${index + 1}`}
          className="sm:mb-1"
          onClick={onRemove}
        >
          Убрать
        </Button>
      )}
    </div>
  )
}

export const LocationsField = () => {
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  // Ключи строк ведёт useFieldArray: без них удаление склада из середины списка оставляло бы
  // набранный в поиске города текст у соседней строки.
  const { fields, append, remove } = useFieldArray({ control, name: 'locations' })
  const locations = watch('locations') ?? []
  // Ошибка приходит либо на весь список (пустой, повтор города), либо на строку без города.
  const rowError = Array.isArray(errors.locations)
    ? errors.locations.find((row) => row?.city?.message)?.city?.message
    : undefined
  const error = errors.locations?.message ?? rowError

  // Проверяем весь список, а не одну строку: повтор города и пустой список — ошибки всего списка,
  // и проверка только изменённой строки оставила бы их висеть после исправления.
  const changeLocation = (index: number, location: LocationForm) => {
    setValue(`locations.${index}`, location, { shouldDirty: true })
    void trigger('locations')
  }

  return (
    <div className="grid gap-4">
      {locations.map((location, index) => (
        <LocationRow
          // Ключ из useFieldArray переживает удаление строки, поэтому набранный в поиске города
          // текст остаётся в своей строке. Списки идут одной длины; номер строки — страховка от
          // падения, если они когда-нибудь разойдутся.
          key={fields[index]?.id ?? `row-${index}`}
          index={index}
          location={location}
          canRemove={locations.length > 1}
          onChange={(next) => changeLocation(index, next)}
          onRemove={() => {
            remove(index)
            // Проверяем список после удаления: повтор города — ошибка всего списка, и без
            // повторной проверки подпись о нём осталась бы на экране.
            void trigger('locations')
          }}
        />
      ))}

      {locations.length < maxLocationCount && (
        <div>
          <Button type="button" onClick={() => append({ city: null, address: '' })}>
            <PlusIcon />
            Добавить город склада
          </Button>
        </div>
      )}

      <FieldError>{error}</FieldError>
    </div>
  )
}
